import { useState } from "react"
import { motion } from "framer-motion"
import { ChevronLeft, ChevronRight, Download, UserPlus, MapPin, Navigation } from "lucide-react"
import { Button } from "./ui/button"

interface HowItWorksSectionProps {
  t: Record<string, any>
}

export function HowItWorksSection({ t }: HowItWorksSectionProps) {
  const [currentSlide, setCurrentSlide] = useState(0)

  const steps = [
    {
      number: "01",
      title: t?.how?.step1 || "Download App",
      description: "Get the app from your app store",
      icon: Download,
    },
    {
      number: "02",
      title: t?.how?.step2 || "Sign Up",
      description: "Create your account in seconds",
      icon: UserPlus,
    },
    {
      number: "03",
      title: t?.how?.step3 || "Book a Ride",
      description: "Request a ride and enjoy",
      icon: MapPin,
    },
  ]

  const screens = [
    {
      title: "Download",
      description: "Available on iOS and Android",
      color: "from-blue-600 to-blue-400",
      icon: Download,
    },
    {
      title: "Sign Up",
      description: "Quick and secure registration",
      color: "from-purple-600 to-purple-400",
      icon: UserPlus,
    },
    {
      title: "Book Ride",
      description: "Find your ride in seconds",
      color: "from-pink-600 to-pink-400",
      icon: MapPin,
    },
    {
      title: "Track Live",
      description: "Real-time ride tracking",
      color: "from-green-600 to-green-400",
      icon: Navigation,
    },
  ]

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % screens.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + screens.length) % screens.length)
  }

  return (
    <section id="how" className="py-20 px-4 bg-gradient-to-b from-black to-slate-900">
      <div className="container mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-white mb-4">{t?.how?.title || "How It Works"}</h2>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-12 items-center">
          {/* Steps */}
          <div className="flex-1 flex flex-col md:flex-row lg:flex-col items-center lg:items-start justify-between gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex-1 text-center lg:text-left"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-5xl font-bold text-blue-500/30">{step.number}</div>
                    <Icon className="h-8 w-8 text-blue-500" />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-400">{step.description}</p>
                </motion.div>
              )
            })}
          </div>

          {/* Carousel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex-1 relative"
          >
            <div className="relative h-96 md:h-[500px] rounded-2xl overflow-hidden">
              {/* Phone frame mockup */}
              <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl border-8 border-slate-700 shadow-2xl">
                {/* Screen content */}
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className={`w-full h-full bg-gradient-to-br ${screens[currentSlide].color} flex flex-col items-center justify-center p-8`}
                >
                  <div className="text-center">
                    <div className="mb-4">
                      {(() => {
                        const Icon = screens[currentSlide].icon
                        return <Icon className="h-16 w-16 text-white mx-auto" />
                      })()}
                    </div>
                    <h3 className="text-3xl font-bold text-white mb-2">{screens[currentSlide].title}</h3>
                    <p className="text-white/80">{screens[currentSlide].description}</p>
                  </div>
                </motion.div>

                {/* Notch */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-slate-900 rounded-b-3xl"></div>
              </div>

              {/* Navigation buttons */}
              <div className="absolute -left-16 top-1/2 transform -translate-y-1/2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={prevSlide}
                  className="rounded-full border-white/20 hover:border-white/40 bg-transparent"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              </div>

              <div className="absolute -right-16 top-1/2 transform -translate-y-1/2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={nextSlide}
                  className="rounded-full border-white/20 hover:border-white/40 bg-transparent"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              {/* Dots indicator */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                {screens.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentSlide ? "bg-white w-6" : "bg-white/40"
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
